import * as THREE from 'three';

import { InputHandler } from '../../input-handler';
import {
  KeyInputData,
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../../utilities/input-event/input-event-data-extractor';
import { FlySpeed } from '../fly-speed';
import { SkyviewPerspectiveCamera } from '../skyview-perspective-camera';
import { CameraControllerStateType } from '../types';
import { OrbitCameraController } from './orbit-controls';

type Movement = 'forward' | 'back' | 'left' | 'right' | 'down' | 'up';

const movementByKey: Record<string, Movement> = {
  w: 'forward',
  s: 'back',
  a: 'left',
  d: 'right',
  q: 'down',
  e: 'up',
};

/** How much faster the camera flies with Shift held. */
const fastFlyFactor = 4;
/** The longest step between two frames, in seconds, so a slow frame doesn't jump. */
const maxFrameStep = 0.1;
/** Radians per unit of pointer movement on screen. */
const lookSpeed = 1.5;
/** The wheel delta of one notch. */
const wheelNotch = 120;

/**
 * Dragging with the left button drags the view, as in a panorama. With the right button the mouse
 * turns the head, as in a game, hidden while it does.
 */
type LookMode = 'drag' | 'head';

/**
 * Flies the camera freely in first person, with no pivot point: W, A, S and D move it forward,
 * left, back and right, Q and E down and up, and dragging looks around from where it is, see
 * LookMode. The wheel moves it towards the point under the cursor. It flies at a constant speed,
 * changed by scrolling with Shift held, and faster while Shift is held.
 */
export class FlyCameraController extends OrbitCameraController {
  private readonly heldMovements = new Set<Movement>();
  private lastUpdate = 0;
  private lookMode: LookMode = 'drag';
  /** Whether the pointer should be hidden, which a lock that is still being taken checks. */
  private pointerHidden = false;
  /** Whether a lock is being taken, so the mouse moves meanwhile don't ask for it again. */
  private lockPending = false;

  constructor(camera: SkyviewPerspectiveCamera) {
    super(camera);
    window.addEventListener('blur', this.releaseKeys);
  }

  dispose() {
    window.removeEventListener('blur', this.releaseKeys);
    this.releaseKeys();
    this.showPointer();
  }

  onKeyDown(input: KeyInputData) {
    if (input.ctrl || input.metaKey || input.alt || isTyping(input.e)) {
      return;
    }

    const movement = movementByKey[input.key.toLowerCase()] as Movement | undefined;
    if (!movement) {
      return;
    }

    if (this.heldMovements.size === 0) {
      this.lastUpdate = performance.now();
    }
    this.heldMovements.add(movement);
    input.handled = true;
  }

  onKeyUp(input: KeyInputData) {
    const movement = movementByKey[input.key.toLowerCase()] as Movement | undefined;
    if (movement) {
      this.heldMovements.delete(movement);
    }
  }

  update() {
    if (this.heldMovements.size === 0) {
      return false;
    }

    const now = performance.now();
    const step = Math.min((now - this.lastUpdate) / 1000, maxFrameStep);
    this.lastUpdate = now;

    const direction = new THREE.Vector3();
    const forward = this.camera.getWorldDirection(new THREE.Vector3());
    // The camera isn't rolled, so its right is level
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(
      this.camera.getWorldQuaternion(new THREE.Quaternion()),
    );
    const up = new THREE.Vector3(0, 0, 1);
    const vectors: Record<Movement, THREE.Vector3> = {
      forward,
      back: forward.clone().negate(),
      right,
      left: right.clone().negate(),
      up,
      down: up.clone().negate(),
    };
    for (const movement of this.heldMovements) {
      direction.add(vectors[movement]);
    }
    if (direction.lengthSq() === 0) {
      return false;
    }

    const speed = FlySpeed.instance.speed * (InputHandler.instance.shiftIsDown ? fastFlyFactor : 1);
    this.camera.move(direction.normalize().multiplyScalar(speed * step));
    this.unfocusAsset();
    this.onTransitionStop();
    return true;
  }

  onMouseDown(input: MouseInputData) {
    this.state =
      input.button.type === MouseButtonType.MiddleButton
        ? CameraControllerStateType.Panning
        : CameraControllerStateType.Rotating;
    this.lookMode = input.button.type === MouseButtonType.RightButton ? 'head' : 'drag';
    return false;
  }

  onMouseMove(input: MouseInputData) {
    if (this.state !== CameraControllerStateType.Rotating) {
      return super.onMouseMove(input);
    }

    if (this.lookMode === 'drag') {
      this.lookAround(input.pointer.delta);
      return true;
    }

    // Hidden once it moves, so a right click without moving still opens the context menu
    const delta = this.pointerDelta(input);
    if (delta.lengthSq() > 0) {
      this.hidePointer();
    }
    // Turns the head, the other way from dragging the view
    this.lookAround(delta.negate());
    return true;
  }

  onMouseUp(input: MouseInputData) {
    this.state = CameraControllerStateType.Idle;
    this.showPointer();
    this.onTransitionStop();
    return false;
  }

  onMouseWheel(input: MouseInputData) {
    if (InputHandler.instance.shiftIsDown) {
      FlySpeed.instance.change(input.wheel.delta / wheelNotch);
      // The camera doesn't move
      return false;
    }
    return super.onMouseWheel(input);
  }

  onTouchMove(input: TouchInputData) {
    if (input.touches.length === 1) {
      // The touch delta is from the previous position, the pointer's to it
      this.lookAround(input.center!.delta.clone().negate());
      this.state = CameraControllerStateType.Rotating;
      return true;
    }
    return super.onTouchMove(input);
  }

  /**
   * Drags the view, as in a panorama: dragging right looks left, dragging up looks down.
   * @param delta From the pointer's position to its previous one, on screen.
   */
  private lookAround(delta: THREE.Vector2) {
    this.camera.lookAround(delta.x * lookSpeed, -delta.y * lookSpeed);
    this.unfocusAsset();
  }

  /**
   * From the pointer's position to its previous one, on screen, as MouseInputData's delta. While
   * the pointer is locked its position stays, so it is taken from how far the mouse moved.
   */
  private pointerDelta(input: MouseInputData) {
    const element = InputHandler.instance.domElement;
    const { movement } = input.pointer;
    if (!element || document.pointerLockElement !== element || !movement) {
      return input.pointer.delta.clone();
    }
    return new THREE.Vector2(
      (-2 * movement.x) / element.clientWidth,
      (2 * movement.y) / element.clientHeight,
    );
  }

  /** Locks the pointer to the view, which hides it and keeps it from leaving the view. */
  private hidePointer() {
    this.pointerHidden = true;
    const element = InputHandler.instance.domElement;
    if (
      !element?.requestPointerLock ||
      document.pointerLockElement === element ||
      this.lockPending
    ) {
      return;
    }

    // The lock arrives later: released then if the drag ended, or another controller took over,
    // while it was being taken
    this.lockPending = true;
    const settle = () => {
      if (!this.lockPending) {
        return;
      }
      this.lockPending = false;
      document.removeEventListener('pointerlockchange', onChange);
      document.removeEventListener('pointerlockerror', settle);
      if (!this.pointerHidden) {
        this.showPointer();
      }
    };
    // Not when an earlier lock is released
    const onChange = () => {
      if (document.pointerLockElement === element) {
        settle();
      }
    };
    document.addEventListener('pointerlockchange', onChange);
    // Refused e.g. right after the user left a lock with Escape, the pointer then shows
    document.addEventListener('pointerlockerror', settle);

    try {
      // A promise in newer browsers, nothing in older ones, which only send the events
      const request: unknown = element.requestPointerLock();
      if (request instanceof Promise) {
        request.then(settle, settle);
      }
    } catch {
      settle();
    }
  }

  private showPointer() {
    this.pointerHidden = false;
    const element = InputHandler.instance.domElement;
    if (element && document.pointerLockElement === element) {
      document.exitPointerLock();
    }
  }

  private readonly releaseKeys = () => {
    this.heldMovements.clear();
  };
}

/** Whether a key goes to a text field rather than the view. */
function isTyping(e: KeyboardEvent) {
  const target = e.target;
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}
